import React, { useEffect } from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";

import IconClose from "@material-ui/icons/Close";
import IconCheck from "@material-ui/icons/Check";

import moment from "moment";

import { genericApi } from "../lib/genericAPI";
import { labtesttype } from "../lib/storage/labtest";

import { LabTestFullType } from "../lib/api/labtest";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: `${theme.spacing.unit * 2}px`,
      maxWidth: 720,
      margin: "0 auto",
      width: "100%",
      boxSizing: "border-box",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: `${theme.spacing.unit}px`,
      paddingBottom: `${theme.spacing.unit}px`,
      marginBottom: `${theme.spacing.unit * 2}px`,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    title: {
      fontSize: "1.1rem",
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    actions: {
      display: "flex",
      gap: `${theme.spacing.unit}px`,
    },
    closeButton: {
      minWidth: "44px",
      backgroundColor: "#ffebee",
      borderColor: "#e53935",
      color: "#c62828",
      "&:hover": {
        backgroundColor: "#ffcdd2",
        borderColor: "#c62828",
      },
    },
    confirmButton: {
      minWidth: "44px",
      backgroundColor: "#43a047",
      color: "white",
      "&:hover": {
        backgroundColor: "#2e7d32",
      },
    },
    naButton: {
      minWidth: "44px",
      backgroundColor: "#fb8c00",
      color: "white",
      "&:hover": {
        backgroundColor: "#ef6c00",
      },
    },
    tableCard: {
      width: "100%",
      overflow: "hidden",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    headRow: {
      backgroundColor: theme.palette.grey[100],
    },
    headCell: {
      textAlign: "left",
      fontSize: "12px",
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      padding: `${theme.spacing.unit}px ${theme.spacing.unit * 1.5}px`,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    bodyRow: {
      "&:nth-child(even)": {
        backgroundColor: theme.palette.grey[50],
      },
    },
    bodyCell: {
      fontSize: "13px",
      padding: `${theme.spacing.unit}px ${theme.spacing.unit * 1.5}px`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
    },
    dateCell: {
      whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
    },
    emptyState: {
      textAlign: "center",
      padding: `${theme.spacing.unit * 4}px`,
      color: theme.palette.text.secondary,
      fontSize: "14px",
    },
  });

type InspectionLabTestProps = {
  producer_id: number;
  puc_id: number;
  orchard_id: number;
  variety_id: number;
  handleAction: (value: number) => void;
} & WithStyles<typeof styles>;

const InspectionLabTestUnstyled: React.SFC<InspectionLabTestProps> = (props) => {
  const { classes, producer_id, puc_id, orchard_id, variety_id, handleAction } = props;
  const [labtestd, setLabtestd] = React.useState([]);

  useEffect(() => {
    const labtestGeneric = genericApi<labtesttype>("labtest");

    labtestGeneric.all().then((labtestLocal) => {
      const resultlabtest = labtestLocal
        .filter((item) => item.producer_id == producer_id)
        .filter((item) => item.puc_id == puc_id)
        .filter((item) => item.orchard_id == orchard_id)
        .filter((item) => item.variety_id == variety_id)
        .sort((a, b) => {
          if (a.sampledateend > b.sampledateend) return 1;
          if (a.sampledateend < b.sampledateend) return -1;
          return 1;
        });
      if (resultlabtest && resultlabtest.length > 0) {
        setLabtestd(resultlabtest);
      }
    });
  }, [producer_id, puc_id, orchard_id, variety_id]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography component="h2" className={classes.title}>
          Lab Test
        </Typography>
        <div className={classes.actions}>
          <Button variant="outlined" size="small" className={classes.closeButton} onClick={() => handleAction(0)}>
            <IconClose />
          </Button>
          <Button variant="contained" size="small" className={classes.naButton} onClick={() => handleAction(2)}>
            N/A
          </Button>
          <Button variant="contained" size="small" className={classes.confirmButton} onClick={() => handleAction(1)}>
            <IconCheck />
          </Button>
        </div>
      </div>

      <Paper elevation={1} className={classes.tableCard}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.headRow}>
              <th className={classes.headCell}>Date</th>
              <th className={classes.headCell}>Lab</th>
              <th className={classes.headCell}>Type</th>
              <th className={classes.headCell}>Approved</th>
            </tr>
          </thead>
          <tbody>
            {labtestd.length === 0 && (
              <tr>
                <td className={classes.emptyState} colSpan={4}>
                  No lab tests found for this orchard / variety.
                </td>
              </tr>
            )}
            {labtestd.map((item: LabTestFullType) => {
              return (
                <tr key={`det${item.id}`} className={classes.bodyRow}>
                  <td className={`${classes.bodyCell} ${classes.dateCell}`}>
                    {`${moment(item.sampledate).format("DD/MM/YYYY")} – ${moment(item.sampledateend).format("DD/MM/YYYY")}`}
                  </td>
                  <td className={classes.bodyCell}>{item.lab}</td>
                  <td className={classes.bodyCell}>{item.labtesttype_detail}</td>
                  <td className={classes.bodyCell}>
                    {item.approved == 1 ? <IconCheck style={{ color: "#2e7d32" }} /> : <IconClose style={{ color: "#c62828" }} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Paper>
    </div>
  );
};

export default withStyles(styles)(InspectionLabTestUnstyled);
